import cn from 'classnames';
import { useState, useEffect, useRef, RefObject, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';

import { useCheckoutContext, Article, Fee, CartMessages } from '@zooplus/checkout-state';
import { isBitiba } from '@zooplus/commons-lang';
import CouponsComponent from '@zooplus/coupons';
import {
  FlatBox,
  H3,
  P1,
  P2,
  CartButton,
  ProductStandardPrice,
  ProductPrice,
} from '@zooplus/ui-components';

import CartLogisticsFees from '../../cart/cart-logistics-fees';
import CartShippingFeesPopover from '../../cart/cart-shipping-fees-popover';
import CashOnDeliveryFees from '../../common/cash-on-delivery-fees';
import FeedbackMessage from '../../common/notifications/feedback-message';
import PreviewPlaceOrderButton from '../../preview/preview-place-order-button';

import { getAppConfig } from '@config/get-app-config';
import useCartTracking from '@hooks/use-adobe-tracking/use-cart-tracking';
import { useIsResponsiveViewport } from '@hooks/use-is-responsive-viewport';
import { useTranslation } from '@hooks/use-translations';
import commonStyles from '@styles/common.module.scss';
import {
  FEE_TYPE,
  MESSAGE_PLACEMENT,
  CHECKOUT_STATE_PROJECT_ID,
  TRACKING_EVENT_TYPE,
  MINI_AUTOSHIPMENT_BOX,
  PAGE_TYPE,
  MESSAGE_CODE,
  CART_VERSION,
  CUSTOMER_TYPE,
} from '@utilities/constants';
import { hasMessageWithCode } from '@utilities/utilities';
import {
  goToPreview,
  isArrayNotEmpty,
  isObjectNotNullOrEmpty,
  goToNewPreview,
} from '@utilities/utils';

import styles from './order-summary.module.scss';

type OrderSummaryProps = {
  stickyCheckoutButtonRef?: RefObject<HTMLDivElement>;
};

const OrderSummary = ({ stickyCheckoutButtonRef }: OrderSummaryProps) => {
  const {
    cartVersion,
    checkoutState,
    isFetching,
    mainTogglesMap = {},
    isStickyAutoshipmentPresent,
    isSubscriptionValid,
    pageType,
  } = useCheckoutContext(CHECKOUT_STATE_PROJECT_ID);

  const [disableOverviewButton, setDisableOverviewButton] = useState<boolean>(false);
  const [shouldButtonPlaceAtBottom, setShouldButtonPlaceAtBottom] = useState<boolean>(false);
  const [isAutoshipmentDivEmpty, setIsAutoshipmentDivEmpty] = useState<boolean>(true);
  const isResponsiveViewport = useIsResponsiveViewport();

  const orderSummaryRef = useRef<HTMLInputElement>(null);
  const { t } = useTranslation();
  const { trackGoToPreviewButton } = useCartTracking();
  const { site } = getAppConfig();
  const { CLICK_EVENT, IMPRESSION_EVENT } = TRACKING_EVENT_TYPE;

  const articles = checkoutState?.cart?.articles;
  const fees = checkoutState?.cart?.fees;
  const logisticFee = checkoutState?.cart?.logisticFee;
  const formattedSubtotal = checkoutState?.cart?.summary?.formattedSubtotal;
  const formattedGrandTotal = checkoutState?.cart?.summary?.formattedGrandTotal;
  const doublePricing = checkoutState?.cart?.summary?.doublePricing;
  const cartMessages = checkoutState?.cart?.cartMessages;
  const pageTypePreview: boolean = pageType === PAGE_TYPE.PREVIEW;
  const pageTypeCart: boolean = pageType === PAGE_TYPE.WEB_CART;
  const isNewPreviewPage = mainTogglesMap.CHECKOUT_FRONTEND_NEW_PREVIEW_PAGE;
  const isNewPricingEnabled = mainTogglesMap.NEW_PRICING_COMPONENT_IN_CART_AND_PREVIEW;
  const isCartApiV2: boolean = cartVersion === CART_VERSION.V2;
  const isGuestCustomer = checkoutState?.customer?.customerType === CUSTOMER_TYPE.GUEST_CUSTOMER;

  const hasArticles = useMemo(() => isArrayNotEmpty(articles as Article[]), [articles]);

  const hasSubscriptionArticles = useMemo(() => {
    if (isCartApiV2) {
      return (
        articles?.some((article) => article.lineItemDiscounts?.autoshipmentDiscount?.applied) ??
        false
      );
    }

    return articles?.some((article) => article.subscriptionApplied) ?? false;
  }, [articles, isCartApiV2]);

  const isMinOrderError = useMemo(
    () =>
      hasMessageWithCode(checkoutState?.cart?.cartMessages, MESSAGE_CODE.CHECKOUT_ERROR_MINORDER),
    [checkoutState?.cart?.cartMessages],
  );

  const isOrderSummarySticky = useCallback(() => {
    const summaryEl = orderSummaryRef?.current;
    const scrollTop = window.scrollY;
    const summaryTop = summaryEl?.getBoundingClientRect()?.top;

    if (summaryTop && scrollTop >= summaryTop - 10) {
      summaryEl?.classList.add(`${styles.isSticky}`);
    } else {
      summaryEl?.classList.remove(`${styles.isSticky}`);
    }
  }, [orderSummaryRef]);

  useEffect(() => {
    if (!isResponsiveViewport) {
      window.addEventListener('scroll', isOrderSummarySticky);
    }

    return () => {
      window.removeEventListener('scroll', isOrderSummarySticky);
    };
  }, [isResponsiveViewport, isOrderSummarySticky]);

  useEffect(() => {
    const handleScroll = () => {
      const orderSummaryRect = orderSummaryRef.current?.getBoundingClientRect();
      const isOrderSummarySticky = orderSummaryRef?.current?.classList.contains(
        `${styles.isSticky}`,
      );

      if (orderSummaryRect && orderSummaryRect.top <= 0 && isOrderSummarySticky) {
        setShouldButtonPlaceAtBottom(true);
      } else {
        setShouldButtonPlaceAtBottom(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  useEffect(() => {
    const goToPreviewButtonStatus =
      isArrayNotEmpty(cartMessages as CartMessages[]) &&
      cartMessages?.some(
        (currentValue: { blockedOnOverview: boolean }) => currentValue?.blockedOnOverview,
      );

    trackGoToPreviewButton(IMPRESSION_EVENT, goToPreviewButtonStatus || false);
  }, [isResponsiveViewport, isOrderSummarySticky]);

  useEffect(() => {
    const element = document.getElementById(MINI_AUTOSHIPMENT_BOX.IS_NOT_STICKY);
    setIsAutoshipmentDivEmpty(!element?.hasChildNodes());
  });

  const handleGoToPreview = useCallback(() => {
    (async () => {
      if (isNewPreviewPage) {
        goToNewPreview();
        return;
      }
      trackGoToPreviewButton(CLICK_EVENT);
      goToPreview(!!isFetching, isNewPreviewPage, hasArticles, isGuestCustomer);
    })();
  }, [CLICK_EVENT, hasArticles, isFetching, isNewPreviewPage, trackGoToPreviewButton]);

  const scrollToSubscriptionOffer = useCallback(() => {
    const element = document.getElementById('subscription-offer-cart-no-interval');
    if (element) {
      window.scroll({
        top: element.offsetTop,
        left: 0,
        behavior: 'smooth',
      });
    }
  }, []);

  const handleCheckoutButtonClick = useCallback(() => {
    (async () => {
      if (!hasSubscriptionArticles || (hasSubscriptionArticles && isSubscriptionValid)) {
        handleGoToPreview();
      } else {
        scrollToSubscriptionOffer();
      }
    })();
  }, [handleGoToPreview, hasSubscriptionArticles, isSubscriptionValid, scrollToSubscriptionOffer]);

  const checkoutButton = (
    <CartButton
      className={cn(
        commonStyles.cartButton,
        commonStyles.cartButton__withPrice,
        isStickyAutoshipmentPresent && commonStyles.cartButton__stickyButtonMarginTop,
      )}
      data-testid="goto-preview-button"
      disabled={disableOverviewButton}
      onClick={handleCheckoutButtonClick}>
      {t('cart.summary.proceedToCheckout')}
      <span className={commonStyles.mobileView}>{formattedGrandTotal}</span>
    </CartButton>
  );

  const renderCheckoutButton = (buttonClass?: string) => {
    return isResponsiveViewport ? (
      isMinOrderError ? (
        checkoutButton
      ) : stickyCheckoutButtonRef?.current ? (
        createPortal(checkoutButton, stickyCheckoutButtonRef.current)
      ) : null
    ) : (
      <div
        className={cn(commonStyles.checkoutButtonSection, commonStyles.cartButtonBox, buttonClass)}>
        {checkoutButton}
      </div>
    );
  };

  const renderGrandTotal = () => {
    const PriceComponent = isNewPricingEnabled ? ProductPrice : ProductStandardPrice;
    const commonProps = {
      'data-testid': 'total-price-value',
      className: cn(
        styles.grandTotalPrice,
        isBitiba(site.domain) && !isNewPricingEnabled && styles.themeBitibaGrandTotalPrice,
      ),
    };

    if (isObjectNotNullOrEmpty(doublePricing)) {
      return (
        <div className={styles.totalCurrencyValue}>
          <PriceComponent {...commonProps} price={doublePricing?.labels?.localCurrency ?? ''} />
          <P1 bold>{`(${doublePricing?.labels?.secondaryCurrency})`}</P1>
        </div>
      );
    }

    return <PriceComponent {...commonProps} price={formattedGrandTotal ?? ''} />;
  };

  return (
    <FlatBox
      id="orderSummary"
      data-testid="order-summary"
      className={cn(styles.orderSummaryWrapper, styles.orderSummaryWrapper_borderStyle)}
      ref={orderSummaryRef}
      rounded
      variant="white">
      <FeedbackMessage
        placement={MESSAGE_PLACEMENT.CART_SUMMARY_HEADER}
        customStyle={cn(styles.summaryMessageWrapper, styles.commonStyle)}
      />

      {pageTypeCart && !isResponsiveViewport && !shouldButtonPlaceAtBottom && (
        <div className={cn(styles.commonStyle)}>
          {renderCheckoutButton('pp-mb-l')}
          <FeedbackMessage
            placement={MESSAGE_PLACEMENT.CART_SUMMARY_FOOTER}
            customStyle="pp-mb-l"
            disableOverviewCallback={setDisableOverviewButton}
          />
        </div>
      )}

      {pageTypePreview && !shouldButtonPlaceAtBottom && !isResponsiveViewport && (
        <PreviewPlaceOrderButton key="placeOrderButton" />
      )}

      <div className={cn(styles.subtotalWrapper, styles.commonStyle, 'pp-mb-l')}>
        <H3 data-testid="overview-sub-total-title">{t('cart.summary.subtotal')}</H3>
        <P1 data-testid="overview-sub-total-value">{formattedSubtotal}</P1>
      </div>

      <div className={cn(styles.commonStyle, 'pp-mb-l')}>
        <CartShippingFeesPopover />
        {isArrayNotEmpty(fees as Fee[]) &&
          fees?.map((fee) => {
            const { type } = fee;
            if (type === FEE_TYPE.LOGISTICS && logisticFee) {
              return <CartLogisticsFees key="logistic-fee" fee={fee} logisticFee={logisticFee} />;
            }
            if (type === FEE_TYPE.PAYMENT) {
              return <CashOnDeliveryFees key="cash-on-delivery-fee" {...fee} />;
            }
            return null;
          })}
      </div>

      <div className={cn(styles.commonStyle, 'pp-mb-l')}>
        <CouponsComponent />
      </div>

      <div
        id={MINI_AUTOSHIPMENT_BOX.IS_NOT_STICKY}
        className={cn(styles.commonStyle, !isAutoshipmentDivEmpty && 'pp-mb-l')}></div>

      <FlatBox
        className={cn(
          styles.totalPriceWrapper,
          styles.commonStyle,
          'pp-mb-l',
          isBitiba(site.domain) && styles.themeBitibaTotalPriceWrapper,
          !pageTypePreview && isAutoshipmentDivEmpty && 'pp-mt-0',
        )}
        variant="white"
        rounded={!isBitiba(site.domain)}
        aria-live="polite"
        aria-atomic="true"
        data-testid="total-price-wrapper">
        <div data-testid="total-price-title" className={styles.totalPriceTitle}>
          <H3 className={styles.heading} data-testid="summary-total-text">
            {t('cart.summary.total')}
          </H3>
          <P2 className={styles.vatText} data-testid="summary-include-VAT-text">
            {t('cart.summary.includeVAT')}
          </P2>
        </div>
        {renderGrandTotal()}
      </FlatBox>

      {pageTypeCart && (shouldButtonPlaceAtBottom || isResponsiveViewport) && (
        <div className={cn(styles.commonStyle)}>
          <FeedbackMessage
            placement={MESSAGE_PLACEMENT.CART_SUMMARY_FOOTER}
            customStyle="pp-mb-l"
            disableOverviewCallback={setDisableOverviewButton}
          />
          {renderCheckoutButton()}
        </div>
      )}

      {pageTypePreview && (shouldButtonPlaceAtBottom || isResponsiveViewport) && (
        <PreviewPlaceOrderButton key="placeOrderButton" />
      )}
    </FlatBox>
  );
};

export default OrderSummary;
